import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

@Injectable({
  providedIn: 'root'
})
export class ThemeService implements OnDestroy {
  private static readonly STORAGE_KEY = 'tbds.theme.preference';
  private readonly preference$ = new BehaviorSubject<ThemePreference>('system');
  private readonly resolvedTheme$ = new BehaviorSubject<ResolvedTheme>('light');
  private mediaQueryList: MediaQueryList | null = null;
  private readonly handleSystemThemeChange = () => this.applyTheme();

  readonly themePreference$ = this.preference$.asObservable();
  readonly isDarkMode$ = this.resolvedTheme$.asObservable();

  constructor() {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.mediaQueryList?.removeEventListener('change', this.handleSystemThemeChange);
  }

  setPreference(preference: ThemePreference): void {
    this.preference$.next(preference);
    localStorage.setItem(ThemeService.STORAGE_KEY, preference);
    this.applyTheme();
  }

  toggleDarkMode(): void {
    const nextPreference = this.resolvedTheme$.value === 'dark' ? 'light' : 'dark';
    this.setPreference(nextPreference);
  }

  getPreference(): ThemePreference {
    return this.preference$.value;
  }

  isDarkMode(): boolean {
    return this.resolvedTheme$.value === 'dark';
  }

  private initialize(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const storedPreference = localStorage.getItem(ThemeService.STORAGE_KEY);
    if (storedPreference === 'light' || storedPreference === 'dark' || storedPreference === 'system') {
      this.preference$.next(storedPreference);
    }

    this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaQueryList.addEventListener('change', this.handleSystemThemeChange);
    this.applyTheme();
  }

  private applyTheme(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const preference = this.preference$.value;
    const resolvedTheme: ResolvedTheme =
      preference === 'system'
        ? (this.mediaQueryList?.matches ? 'dark' : 'light')
        : preference;

    const root = document.documentElement;
    root.classList.toggle('dark-theme', resolvedTheme === 'dark');
    root.classList.toggle('light-theme', resolvedTheme === 'light');
    this.resolvedTheme$.next(resolvedTheme);
  }
}
